import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import { JwtHelperService } from "@auth0/angular-jwt";
import { BehaviorSubject, map, Observable, Subject } from "rxjs";
import { environment } from "src/environments/environment";
import { LoginModel } from "../../models/login/login.model";
import { HttpService } from "../common/http-service";
import { SignupModel } from "../../models/signup/signup.model";
import { SignoutModel } from "../../models/signout/signout.model";
import { UserModel } from "../../models/user/user.model";
import { ResetPasswordModel } from "../../models/reset-password/reset-password.model";
import { ForgotPasswordModel } from "../../models/forgot-password/forgot-password.model";
import { PushNotificationRequestModel } from "../../models/fire-messaging/notification-request.model";
import { ChangePasswordModel } from "../../models/change-password/change-password.model";
import { RefreshTokenModel } from "../../models/refresh-token/refresh-token.model";

const USER_API = environment.apiURL + '/api/mng/User/';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  //#region Fields
  _isLoggedIn$ = new BehaviorSubject<boolean>(false);
  _isSuperAdmin$ = new BehaviorSubject<boolean>(false);
  private readonly access_token = 'resy_access_token';
  public isLoggedIn$ = this._isLoggedIn$.asObservable();
  public isSuperAdmin$ = this._isSuperAdmin$.asObservable();
  get token(): any {
    return localStorage.getItem(this.access_token);
  }
  //#endregion

  //#region Constructor
  constructor(private _httpService: HttpService, private _httpClient: HttpClient, private _jwtHelperService: JwtHelperService) {
    if (this.token && !_jwtHelperService.isTokenExpired(this.token)) {
      this._isLoggedIn$.next(!!this.token);
      if (this.token != null) {
        const tokenDecoded = this._jwtHelperService.decodeToken(this.token);
        this.loginUser.next(new UserModel(tokenDecoded.id, tokenDecoded.rlnm.split(',').map((x: string) => +x)))
      }
      else {
        this.loginUser.next(null);
      }
    }
  }
  //#region Public Methods
  login(model: LoginModel) {
    let url = USER_API + "authenticate";
    return this._httpService.httpPost(url, model)
      .pipe(map(response => {
        return response;
      }));
  }

  confirmPassword(model: ResetPasswordModel) {
    let url = USER_API + "confirmpassword";
    return this._httpService.httpPost(url, model)
      .pipe(map(response => {
        return response;
      }));
  }

  forgotPassword(model: ForgotPasswordModel) {
    let url = USER_API + "forgotpassword";
    return this._httpService.httpPost(url, model)
      .pipe(map(response => {
        return response;
      }));
  }

  changePasswordManually(model: ChangePasswordModel) {
    let url = USER_API + "changepassword";
    return this._httpService.httpPost(url, model)
      .pipe(map(response => {
        return response;
      }));
  }

  signup(model: SignupModel) {
    let url = USER_API + "register";
    return this._httpService.httpPost(url, model)
      .pipe(map(response => {
        return response;
      }));
  }

  logout(model : SignoutModel) {
    let url = USER_API + "signout";
    return this._httpService.httpPost(url, model)
      .pipe(map(response => {
        return response;
      }));
  }

  getAllCountryNames() {
    let url = USER_API + "getallcountrynames";
    return this._httpService.httpGet(url)
      .pipe(map(response => {
        return response;
      }));
  }

  getUserProfile(){
    let url = USER_API + "getuserprofile";
    return this._httpService.httpGet(url)
      .pipe(map(response => {
        return response;
      }));
  }


  registerFCMToken(userId: string, FCMToken: string): Observable<any> {
    let req: PushNotificationRequestModel = new PushNotificationRequestModel();
    req.deviceID = userId;
    req.deviceFCMToken = FCMToken;
    return this._httpService.httpPost(USER_API + 'registerfcmtoken', req)
      .pipe(map(response => {
        return response;
      }));
  }

  uploadProfilePicture(file: any) {
    let url = USER_API +'/updateuserprofilepicture';

    const formData: FormData = new FormData();
    formData.append('File', file as string);
    return this._httpClient
      .post<any>(url, formData, {
        headers: new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem("resy_access_token") as string}`)
      })
      .pipe(map(response => {
        return response;
      }));
  }

  deleteProfilePicture() {
    let url = USER_API +'/deleteuserprofilepicture';

    return this._httpClient
      .post<any>(url, {}, {
        headers: new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem("resy_access_token") as string}`)
      })
      .pipe(map(response => {
        return response;
      }));
  }

  getTermsAndConditions(languageId: number, countryCode: string): Observable<any> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append("LanguageId", languageId);
    queryParams = queryParams.append("CountryCode", countryCode);

    return this._httpClient.get(USER_API + 'gettermsandconditions', {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      params: queryParams
    }).pipe(map(response => {
      return response;
    }));
  }

  refreshToken(refreshToken: RefreshTokenModel){
    let url = USER_API + "refresh-token";
    return this._httpService.httpPost(url, refreshToken)
      .pipe(map(response => {
        return response;
      }));
  }

  //#endregion

  //# Subject EventEmiter
  loginUser = new BehaviorSubject<UserModel | null>(null);
  isSuperAdminUser = new Subject<boolean>();
  showHideMenu = new EventEmitter<boolean>()
  //#endregion
}
